<template>
  <div class="mt-2">
    <WarningDisable permission="browser_history" />
    <div>
      <form
        class="mb-4 flex w-full flex-wrap items-end gap-2"
        @submit="submit_without_reloading"
        @reset="reset_filters"
      >
        <div class="bg-base-200 grow p-2">
          <legend>Từ khóa</legend>
          <label class="input w-full">
            <i class="fa-solid fa-magnifying-glass text-base-content/80"></i>
            <input
              type="text"
              placeholder="Tìm kiếm theo từ khóa"
              name="keyword"
              v-model="keyword"
            />
          </label>
        </div>

        <div class="bg-base-200 flex flex-wrap items-end gap-1 p-2">
          <div>
            <legend>Thời gian truy cập</legend>
            <input
              type="datetime-local"
              class="input w-fit"
              :max="created_to || maxDateTimeLocal()"
              v-model="created_from"
            />
          </div>
          <input
            type="datetime-local"
            class="input w-fit"
            :min="created_from || undefined"
            :max="maxDateTimeLocal()"
            v-model="created_to"
          />
        </div>

        <div class="flex gap-1 p-2">
          <input
            class="btn btn-outline"
            type="reset"
            value="&#8635;"
            title="Đặt lại giá trị"
            :disabled="isLoading"
          />
          <button
            class="btn btn-primary btn-outline"
            type="submit"
            :disabled="!filter_has_changed || isLoading"
          >
            &#10003; Áp dụng
          </button>
        </div>
      </form>

      <div class="flex grow flex-col overflow-x-auto">
        <div class="mb-2 flex items-center justify-between gap-2">
          <div
            class="text-base-content/80 flex items-center gap-2 text-xl font-bold"
          >
            <span>Lịch sử duyệt web</span>
            <span v-if="!isLoading" class="badge badge-sm">
              {{ totalFiltered.toLocaleString("vi-VN") }} bản ghi
            </span>
          </div>
          <button
            class="btn btn-primary btn-outline btn-sm"
            type="button"
            :disabled="isLoading || !actionId"
            @click="openExportDialog"
          >
            <i class="fa-solid fa-download"></i>
            Xuất CSV
          </button>
        </div>

        <Loading v-if="isLoading" />

        <template v-else-if="totalFiltered > 0">
          <div class="space-y-3">
            <div
              v-for="bh in browserHistories"
              :key="bh.id"
              class="card bg-base-100 border-base-300 border shadow-sm transition-shadow hover:shadow-md"
            >
              <div
                class="card-body gap-4 p-4 md:flex-row md:items-center md:justify-between"
              >
                <div class="flex min-w-0 flex-1 items-start gap-3">
                  <div
                    class="bg-base-200 text-base-content flex size-11 shrink-0 items-center justify-center rounded-full"
                  >
                    <BrowserIcon :browser="bh.name" />
                  </div>
                  <div class="min-w-0 space-y-1">
                    <div class="flex flex-wrap items-center gap-2">
                      <div class="font-semibold">
                        {{ capitalize(bh.name) || "N/A" }}
                      </div>
                    </div>
                    <div class="flex flex-wrap items-center gap-2 text-sm">
                      <span class="text-base-content/70">
                        {{ formatExactTime(bh.time) }}
                      </span>
                      <span class="text-base-content/50">
                        {{ formatRelativeTime(bh.time) }}
                      </span>
                    </div>
                    <div
                      class="text-base-content/70 max-w-full text-sm break-all"
                      :title="bh.url"
                    >
                      {{ bh.url }}
                    </div>
                  </div>
                </div>

                <div
                  class="dropdown dropdown-end shrink-0 self-start md:self-center"
                >
                  <div tabindex="0" role="button" class="btn btn-ghost btn-sm">
                    <i class="fa-solid fa-ellipsis-vertical"></i>
                  </div>
                  <ul
                    tabindex="-1"
                    class="dropdown-content menu bg-base-100 rounded-box z-10 w-52 p-2 shadow-sm"
                  >
                    <li>
                      <button
                        type="button"
                        @click="copyTextToClipboard(bh.url ?? '')"
                      >
                        Copy URL
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div v-if="isFetchingNextPage" class="flex justify-center py-4">
            <span
              class="loading loading-spinner loading-lg text-primary"
            ></span>
          </div>
        </template>

        <NotFound v-else />
      </div>

      <dialog ref="exportDialog" class="modal">
        <div class="modal-box max-w-5xl">
          <form method="dialog">
            <button
              class="btn btn-circle btn-ghost btn-sm absolute top-2 right-2"
            >
              ✕
            </button>
          </form>

          <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 class="text-lg font-bold">Xuất CSV</h3>
              <p class="text-base-content/60 text-sm">
                Xuất theo bộ lọc hiện tại:
                {{ totalFiltered.toLocaleString("vi-VN") }} bản ghi
              </p>
            </div>
            <button
              class="btn btn-primary btn-outline btn-sm"
              type="button"
              :disabled="isExporting || !actionId"
              @click="exportBrowserHistory"
            >
              <i class="fa-solid fa-download"></i>
              {{ isExporting ? "Đang xuất..." : "Tạo tệp mới" }}
            </button>
          </div>

          <div class="mb-2 flex items-center justify-between gap-2">
            <div class="text-base-content/80 flex items-center gap-2 font-bold">
              <span>Lịch sử xuất</span>
              <span v-if="!exportHistoryLoading" class="badge badge-sm">
                {{ exportTotalFilter.toLocaleString("vi-VN") }} bản ghi
              </span>
            </div>
            <button
              class="btn btn-ghost btn-sm"
              type="button"
              :disabled="exportHistoryLoading"
              @click="loadExportTasks"
            >
              <i class="fa-solid fa-rotate-right"></i>
              Làm mới
            </button>
          </div>

          <Loading v-if="exportHistoryLoading" />

          <template v-else-if="exportTasks.length > 0">
            <div class="bg-base-100 mb-4 overflow-x-auto">
              <table class="table-pin-rows table-md table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Trạng thái</th>
                    <th>Đã xuất</th>
                    <th>Thời gian tạo</th>
                    <th>Thời gian hoàn tất</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody class="bg-base-200">
                  <tr v-for="(task, idx) in exportTasks" :key="task.id">
                    <th>{{ idx + 1 + (exportPage - 1) * exportPageSize }}</th>
                    <td>
                      <span
                        class="badge badge-sm"
                        :class="exportStatusClass(task.status)"
                      >
                        {{ exportStatusText(task.status) }}
                      </span>
                      <div v-if="task.error" class="text-error mt-1 text-xs">
                        {{ task.error }}
                      </div>
                    </td>
                    <td>
                      {{ task.exported_records ?? 0 }}/{{
                        task.total_records ?? 0
                      }}
                    </td>
                    <td>{{ formatExportTime(task.created) }}</td>
                    <td>{{ formatExportTime(task.completed) }}</td>
                    <td class="text-right">
                      <button
                        class="btn btn-xs btn-outline"
                        type="button"
                        :disabled="task.status !== 'done'"
                        @click="downloadExportTask(task.id)"
                      >
                        <i class="fa-solid fa-download"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <PaginationButtons
              :total_pages="exportTotalPages"
              :current_page="exportPage"
              @page-change="setExportPage"
            />
          </template>

          <div v-else class="text-base-content/60">Chưa có lịch sử xuất</div>
        </div>
        <form method="dialog" class="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>
    </div>
  </div>
</template>

<script setup lang="ts">
import { str_from_query } from "@/api";
import { axios_instance } from "@/api/api";
import Loading from "@/components/LoadingTemplate.vue";
import PaginationButtons from "@/components/PaginationButtons.vue";
import { downloadFileFromUrl } from "@/composables/useDownload";
import NotFound from "@/components/NotFound.vue";
import { router } from "@/router/router";
import { copyTextToClipboard } from "@/types/util";
import { useInfiniteScroll } from "@vueuse/core";
import dayjs from "dayjs";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { toast } from "vue3-toastify";
import WarningDisable from "../WarningDisable.vue";
import BrowserIcon from "./BrowserIcon.vue";
import { useBrowserHistoryInfiniteQuery } from "./useBrowserHistoryInfinite";

type ExportTask = {
  id: string;
  status: "pending" | "processing" | "done" | "failed";
  error?: string;
  total_records?: number;
  exported_records?: number;
  created?: number;
  completed?: number;
};

const exportPageSize = 10;

const capitalize = (text: string | undefined): string => {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};
const exactTimeFormatter = new Intl.DateTimeFormat("vi-VN", {
  dateStyle: "short",
  timeStyle: "medium",
});
const maxDateTimeLocal = () => dayjs().format("YYYY-MM-DDTHH:mm");
const formatExactTime = (time: number | undefined): string => {
  if (!time) return "N/A";
  return exactTimeFormatter.format(new Date(time / 1000));
};
const formatRelativeTime = (time: number | undefined): string => {
  if (!time) return "N/A";
  return dayjs(time / 1000).fromNow();
};
const formatExportTime = (time: number | undefined): string => {
  if (!time) return "N/A";
  return dayjs(time).format("DD/MM/YYYY HH:mm:ss");
};
const exportStatusText = (status: ExportTask["status"]): string => {
  switch (status) {
    case "pending":
      return "Chờ xử lý";
    case "processing":
      return "Đang xử lý";
    case "done":
      return "Hoàn tất";
    case "failed":
      return "Thất bại";
  }
};
const exportStatusClass = (status: ExportTask["status"]): string => {
  switch (status) {
    case "pending":
      return "badge-warning";
    case "processing":
      return "badge-info";
    case "done":
      return "badge-success";
    case "failed":
      return "badge-error";
  }
};

const init_keyword = computed(() => str_from_query("keyword"));
const init_created_from = computed(() => str_from_query("created_from"));
const init_created_to = computed(() => str_from_query("created_to"));

const keyword = ref<string | undefined>(init_keyword.value);
const created_from = ref<string | undefined>(init_created_from.value);
const created_to = ref<string | undefined>(init_created_to.value);
const actionId = computed(() =>
  String(router.currentRoute.value.params.action_id ?? ""),
);
const isExporting = ref(false);
const exportDialog = ref<HTMLDialogElement>();
let exportTaskTimer: number | undefined;

const filters = computed(() => ({
  action_id: String(router.currentRoute.value.params.action_id ?? ""),
  keyword: str_from_query("keyword"),
  created_from: str_from_query("created_from"),
  created_to: str_from_query("created_to"),
  size: 24,
}));

const {
  browserHistories,
  totalFiltered,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
} = useBrowserHistoryInfiniteQuery(filters);

const filter_has_changed = computed(() => {
  return (
    keyword.value !== init_keyword.value ||
    created_from.value !== init_created_from.value ||
    created_to.value !== init_created_to.value
  );
});

const exportTasks = ref<ExportTask[]>([]);
const exportTotalFilter = ref(0);
const exportPage = ref(1);
const exportHistoryLoading = ref(false);
const exportTotalPages = computed(() =>
  Math.max(1, Math.ceil(exportTotalFilter.value / exportPageSize)),
);

const openExportDialog = () => {
  exportDialog.value?.showModal();
  loadExportTasks();
};

const loadExportTasks = async () => {
  if (!actionId.value) return;
  exportHistoryLoading.value = true;
  try {
    const response = await axios_instance.post<{
      data: ExportTask[] | null;
      total_filter: number;
    }>("/api/v1/view/browser-history/export/pagination", {
      action_id: actionId.value,
      page: exportPage.value,
      size: exportPageSize,
    });
    exportTasks.value = response.data.data ?? [];
    exportTotalFilter.value = response.data.total_filter ?? 0;
  } catch (err) {
    toast.error(
      err instanceof Error ? err.message : "Không tải được lịch sử xuất",
    );
  } finally {
    exportHistoryLoading.value = false;
  }
};

const setExportPage = (page: number) => {
  exportPage.value = page;
  loadExportTasks();
};

const downloadExportTask = (id: string) => {
  downloadFileFromUrl(
    `/api/v1/view/browser-history/export/${id}/download`,
    `browser-history-${id}.csv`,
  );
};

const watchExportTask = async (id: string) => {
  try {
    const response = await axios_instance.get<{ data: ExportTask }>(
      `/api/v1/view/browser-history/export/${id}`,
    );
    const task = response.data.data;

    if (!task) {
      isExporting.value = false;
      toast.error("Không tìm thấy tác vụ xuất");
      return;
    }

    if (task.status === "failed") {
      isExporting.value = false;
      toast.error(task.error || "Xuất thất bại");
      return;
    }

    if (task.status === "done") {
      isExporting.value = false;
      toast.info("Bắt đầu tải xuống tệp...");
      await loadExportTasks();
      downloadExportTask(id);
      return;
    }

    exportTaskTimer = window.setTimeout(() => {
      watchExportTask(id);
    }, 1000);
  } catch (err) {
    isExporting.value = false;
    toast.error(err instanceof Error ? err.message : "Xuất thất bại");
  }
};

const exportBrowserHistory = async () => {
  if (!actionId.value || isExporting.value) return;

  const createdFrom = str_from_query("created_from");
  const createdTo = str_from_query("created_to");
  const parsedCreatedFrom = createdFrom ? Date.parse(createdFrom) : undefined;
  const parsedCreatedTo = createdTo ? Date.parse(createdTo) : undefined;

  if (createdFrom && Number.isNaN(parsedCreatedFrom)) {
    toast.error("Ngày bắt đầu không hợp lệ");
    return;
  }
  if (createdTo && Number.isNaN(parsedCreatedTo)) {
    toast.error("Ngày kết thúc không hợp lệ");
    return;
  }
  if (parsedCreatedFrom !== undefined && parsedCreatedFrom >= Date.now()) {
    toast.error("Ngày bắt đầu phải nhỏ hơn hiện tại");
    return;
  }
  if (
    parsedCreatedFrom !== undefined &&
    parsedCreatedTo !== undefined &&
    parsedCreatedTo <= parsedCreatedFrom
  ) {
    toast.error("Ngày kết thúc phải lớn hơn ngày bắt đầu");
    return;
  }

  isExporting.value = true;
  try {
    const response = await axios_instance.post<{ data: { id: string } }>(
      "/api/v1/view/browser-history/export",
      {
        action_id: actionId.value,
        keyword: str_from_query("keyword"),
        created_from: parsedCreatedFrom,
        created_to: parsedCreatedTo,
      },
    );
    const taskId = response.data.data?.id;
    if (!taskId) {
      isExporting.value = false;
      toast.error("Không tạo được tác vụ xuất");
      return;
    }

    exportPage.value = 1;
    await loadExportTasks();
    watchExportTask(taskId);
  } catch (err) {
    isExporting.value = false;
    toast.error(err instanceof Error ? err.message : "Xuất thất bại");
  }
};

const reset_filters = (e: Event) => {
  e.preventDefault();
  keyword.value = undefined;
  created_from.value = undefined;
  created_to.value = undefined;
  router.push({
    name: router.currentRoute.value.name,
    query: {
      ...router.currentRoute.value.query,
      page: undefined,
      keyword: undefined,
      created_from: undefined,
      created_to: undefined,
    },
  });
};

const submit_without_reloading = (e: Event) => {
  e.preventDefault();
  router.push({
    name: router.currentRoute.value.name,
    query: {
      ...router.currentRoute.value.query,
      page: undefined,
      keyword: keyword.value,
      created_from: created_from.value,
      created_to: created_to.value,
    },
  });
};

onBeforeUnmount(() => {
  if (exportTaskTimer) window.clearTimeout(exportTaskTimer);
});

onMounted(() => {
  loadExportTasks();

  const scrollContainer = document.querySelector("main");
  if (!scrollContainer) return;

  useInfiniteScroll(
    scrollContainer,
    () => {
      if (hasNextPage.value && !isFetchingNextPage.value) {
        fetchNextPage();
      }
    },
    { distance: 100 },
  );
});
</script>
